// Pure helpers for composing a report definition. No data is fetched while composing: everything here works
// on the catalog (objects, fields, relations) and the definition only.
import type {
  Catalog,
  CatalogEntity,
  CatalogField,
  CatalogRelation,
  ColumnSpec,
  ConditionSpec,
  Display,
  FilterGroupSpec,
  MeasureKind,
  MeasureSpec,
  ReportSpec,
  SortDirection,
  SummaryGroupSpec,
  SummarySpec,
} from "./types";

export const MAX_COLUMNS = 30;
export const MAX_CONDITIONS = 20;
export const MAX_SORTS = 3;
export const MAX_SUMMARY_GROUPS = 2;
export const MAX_MEASURES = 5;

export type ResolvedPath = {
  path: string;
  /** Relations walked, in order. */
  relations: CatalogRelation[];
  /** The field at the end, or null for a relation with several values itself (counted). */
  field: CatalogField | null;
  /** The object the field belongs to. */
  entity: CatalogEntity;
  /** True when the path goes through a relation with several values (shown as a list, tested as "has one"). */
  many: boolean;
};

export function sourceEntity(catalog: Catalog, sourceKey: string): CatalogEntity | null {
  const source = catalog.sources.find((item) => item.key === sourceKey);
  return source ? catalog.entities[source.entity] ?? null : null;
}

/** Resolves a path against the user's catalog; null when it is unknown or not available to this user. */
export function resolvePath(catalog: Catalog, sourceKey: string, path: string): ResolvedPath | null {
  let entity = sourceEntity(catalog, sourceKey);
  if (!entity || !path) return null;
  const parts = path.split(".");
  const relations: CatalogRelation[] = [];
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index];
    const last = index === parts.length - 1;
    const relation = entity.relations.find((item) => item.key === part);
    if (relation) {
      if (relation.cardinality === "MANY" && relations.length > 0) return null;
      const target = catalog.entities[relation.entity];
      if (!target) return null;
      relations.push(relation);
      entity = target;
      if (last) {
        return relation.cardinality === "MANY"
          ? { path, relations, field: null, entity, many: true }
          : null;
      }
      continue;
    }
    const field = entity.fields.find((item) => item.key === part);
    if (!field || !last) return null;
    return { path, relations, field, entity, many: relations.some((item) => item.cardinality === "MANY") };
  }
  return null;
}

export function displayFor(resolved: ResolvedPath): Display {
  if (!resolved.field) return "COUNT";
  return resolved.many ? "LIST" : "VALUE";
}

/** A new definition for a subject, starting from its defaults (only the parts this user may use). */
export function newDefinition(catalog: Catalog, sourceKey: string): ReportSpec {
  const source = catalog.sources.find((item) => item.key === sourceKey);
  const known = (path: string) => resolvePath(catalog, sourceKey, path) !== null;
  const columns: ColumnSpec[] = (source?.defaultColumns ?? [])
    .filter((column) => known(column.path))
    .map((column) => ({ path: column.path, display: (column.display as Display) ?? null }));
  const conditions: ConditionSpec[] = (source?.defaultConditions ?? [])
    .filter((condition) => known(condition.path))
    .map((condition) => ({
      path: condition.path,
      operator: condition.operator,
      value: condition.value && condition.value.length > 0 ? [...condition.value] : null,
    }));
  const sort = (source?.defaultSort ?? []).filter((item) => known(item.path)).map((item) => ({ ...item }));
  return {
    version: 1,
    source: sourceKey,
    context: { schoolYear: { mode: "CURRENT", id: null } },
    columns,
    filter: { match: "ALL", conditions, groups: [] },
    sort,
  };
}

// ===== Columns =====

export function hasColumn(definition: ReportSpec, path: string): boolean {
  return definition.columns.some((column) => column.path === path);
}

export function toggleColumn(definition: ReportSpec, path: string): ReportSpec {
  if (hasColumn(definition, path)) {
    return { ...definition, columns: definition.columns.filter((column) => column.path !== path) };
  }
  if (definition.columns.length >= MAX_COLUMNS) return definition;
  return { ...definition, columns: [...definition.columns, { path }] };
}

export function moveColumn(definition: ReportSpec, from: number, to: number): ReportSpec {
  if (from === to || from < 0 || to < 0 || from >= definition.columns.length || to >= definition.columns.length) {
    return definition;
  }
  const columns = [...definition.columns];
  const [moved] = columns.splice(from, 1);
  columns.splice(to, 0, moved);
  return { ...definition, columns };
}

export function renameColumn(definition: ReportSpec, path: string, label: string): ReportSpec {
  const cleaned = label.trim().slice(0, 80);
  return {
    ...definition,
    columns: definition.columns.map((column) =>
      column.path === path ? { ...column, label: cleaned || null } : column),
  };
}

// ===== Conditions =====

export function conditionCount(filter: FilterGroupSpec): number {
  return filter.conditions.length + (filter.groups ?? []).reduce((sum, group) => sum + group.conditions.length, 0);
}

/** groupIndex null = the top level; otherwise a group. */
export function addCondition(definition: ReportSpec, condition: ConditionSpec, groupIndex: number | null = null): ReportSpec {
  if (conditionCount(definition.filter) >= MAX_CONDITIONS) return definition;
  if (groupIndex === null) {
    return { ...definition, filter: { ...definition.filter, conditions: [...definition.filter.conditions, condition] } };
  }
  return updateGroup(definition, groupIndex, (group) => ({ ...group, conditions: [...group.conditions, condition] }));
}

export function replaceCondition(definition: ReportSpec, groupIndex: number | null, index: number,
                                 condition: ConditionSpec): ReportSpec {
  if (groupIndex === null) {
    const conditions = definition.filter.conditions.map((item, i) => (i === index ? condition : item));
    return { ...definition, filter: { ...definition.filter, conditions } };
  }
  return updateGroup(definition, groupIndex, (group) => ({
    ...group,
    conditions: group.conditions.map((item, i) => (i === index ? condition : item)),
  }));
}

export function removeCondition(definition: ReportSpec, groupIndex: number | null, index: number): ReportSpec {
  if (groupIndex === null) {
    const conditions = definition.filter.conditions.filter((_, i) => i !== index);
    return { ...definition, filter: { ...definition.filter, conditions } };
  }
  return updateGroup(definition, groupIndex, (group) => ({
    ...group,
    conditions: group.conditions.filter((_, i) => i !== index),
  }));
}

export function setMatch(definition: ReportSpec, groupIndex: number | null, match: "ALL" | "ANY"): ReportSpec {
  if (groupIndex === null) return { ...definition, filter: { ...definition.filter, match } };
  return updateGroup(definition, groupIndex, (group) => ({ ...group, match }));
}

export function addGroup(definition: ReportSpec): ReportSpec {
  const groups = definition.filter.groups ?? [];
  if (groups.length >= 10) return definition;
  return { ...definition, filter: { ...definition.filter, groups: [...groups, { match: "ANY", conditions: [] }] } };
}

export function removeGroup(definition: ReportSpec, groupIndex: number): ReportSpec {
  const groups = (definition.filter.groups ?? []).filter((_, i) => i !== groupIndex);
  return { ...definition, filter: { ...definition.filter, groups } };
}

function updateGroup(definition: ReportSpec, groupIndex: number,
                     update: (group: FilterGroupSpec) => FilterGroupSpec): ReportSpec {
  const groups = (definition.filter.groups ?? []).map((group, i) => (i === groupIndex ? update(group) : group));
  return { ...definition, filter: { ...definition.filter, groups } };
}

/** Operators the backend allows for a path (from the catalog). */
export function operatorsFor(catalog: Catalog, resolved: ResolvedPath | null): string[] {
  if (!resolved) return [];
  if (!resolved.field) return catalog.relationOperators ?? [];
  if (!resolved.field.filterable) return [];
  return catalog.operators[resolved.field.type] ?? [];
}

export type ValueKind = "NONE" | "SINGLE" | "RANGE" | "DAYS" | "LIST";

export function valueKind(operator: string): ValueKind {
  switch (operator) {
    case "BETWEEN":
      return "RANGE";
    case "LAST_DAYS":
    case "NEXT_DAYS":
      return "DAYS";
    case "ANY_OF":
    case "NONE_OF":
      return "LIST";
    case "IN_SCHOOL_YEAR":
    case "IS_TRUE":
    case "IS_FALSE":
    case "IS_EMPTY":
    case "IS_NOT_EMPTY":
    case "HAS_ANY":
    case "HAS_NONE":
      return "NONE";
    default:
      return "SINGLE";
  }
}

export function isConditionComplete(condition: ConditionSpec): boolean {
  const value = condition.value;
  switch (valueKind(condition.operator)) {
    case "NONE":
      return true;
    case "SINGLE":
      return value !== null && value !== undefined && String(value).trim() !== "" && !Array.isArray(value);
    case "DAYS":
      return typeof value === "number" ? value >= 1 && value <= 3650 : Number(value) >= 1 && Number(value) <= 3650;
    case "RANGE":
      return Array.isArray(value) && value.length === 2 && value.every((item) => String(item ?? "").trim() !== "");
    case "LIST":
      return Array.isArray(value) && value.length > 0;
    default:
      return false;
  }
}

// ===== Sorting =====

export function setSort(definition: ReportSpec, path: string, direction: SortDirection | null): ReportSpec {
  const others = definition.sort.filter((item) => item.path !== path);
  if (!direction) return { ...definition, sort: others };
  return { ...definition, sort: [{ path, direction }, ...others].slice(0, MAX_SORTS) };
}

export function sortableColumn(resolved: ResolvedPath | null): boolean {
  if (!resolved) return false;
  if (!resolved.field) return true;
  return !resolved.many && resolved.field.sortable;
}

// ===== Validity and comparison =====

export type DefinitionProblem = "NO_COLUMNS" | "NO_GROUPS" | "INCOMPLETE_CONDITION" | "UNAVAILABLE_PATH";

/** Problems that stop "Run report" and "Save". */
export function definitionProblems(catalog: Catalog, definition: ReportSpec): DefinitionProblem[] {
  const problems: DefinitionProblem[] = [];
  const summary = definition.summary ?? null;
  if (summary) {
    if (summary.groups.length === 0) problems.push("NO_GROUPS");
  } else if (definition.columns.length === 0) {
    problems.push("NO_COLUMNS");
  }
  const conditions = [
    ...definition.filter.conditions,
    ...(definition.filter.groups ?? []).flatMap((group) => group.conditions),
  ];
  if (conditions.some((condition) => !isConditionComplete(condition))) problems.push("INCOMPLETE_CONDITION");
  const paths = summary
    ? [
      ...summary.groups.map((group) => group.path),
      ...summary.measures.filter((measure) => measure.path).map((measure) => measure.path as string),
      ...conditions.map((condition) => condition.path),
    ]
    : [
      ...definition.columns.map((column) => column.path),
      ...conditions.map((condition) => condition.path),
      ...definition.sort.map((item) => item.path),
    ];
  if (paths.some((path) => !resolvePath(catalog, definition.source, path))) problems.push("UNAVAILABLE_PATH");
  return problems;
}

/** Drops parts this user cannot use (an older or someone else's report); returns what was dropped. */
export function withoutUnavailable(catalog: Catalog, definition: ReportSpec): { definition: ReportSpec; dropped: string[] } {
  const ok = (path: string) => resolvePath(catalog, definition.source, path) !== null;
  const dropped = new Set<string>();
  const keep = <T extends { path: string }>(items: T[]) => items.filter((item) => {
    if (ok(item.path)) return true;
    dropped.add(item.path);
    return false;
  });
  let summary = definition.summary ?? null;
  if (summary) {
    const groups = keep(summary.groups);
    const measures = summary.measures.filter((measure) => {
      if (!measure.path || ok(measure.path)) return true;
      dropped.add(measure.path);
      return false;
    });
    const changed = groups.length !== summary.groups.length || measures.length !== summary.measures.length;
    summary = { groups, measures: measures.length ? measures : [{ kind: "COUNT" }], sort: changed ? [] : summary.sort };
  }
  return {
    definition: {
      ...definition,
      summary,
      columns: keep(definition.columns),
      sort: keep(definition.sort),
      filter: {
        ...definition.filter,
        conditions: keep(definition.filter.conditions),
        groups: (definition.filter.groups ?? []).map((group) => ({ ...group, conditions: keep(group.conditions) })),
      },
    },
    dropped: Array.from(dropped),
  };
}

/** A stable key of what a run depends on; renaming a column or the report does not count. */
export function runKey(definition: ReportSpec): string {
  return JSON.stringify({
    source: definition.source,
    context: definition.context,
    columns: definition.columns.map((column) => [column.path, column.display ?? null]),
    filter: definition.filter,
    sort: definition.sort,
    summary: definition.summary ?? null,
  });
}

// ===== Summary =====

export type PathOption = { path: string; label: string; group: string; isDate: boolean };

/**
 * What a summary can group by: single values of the subject and of its ONE relations, and the number of a
 * relation with several values (e.g. number of children). Lists, sensitive data and computed values cannot.
 */
export function groupOptions(catalog: Catalog, sourceKey: string,
                             label: (item: { labelKey: string; defaultLabel: string }) => string,
                             countLabel: (name: string) => string): PathOption[] {
  const root = sourceEntity(catalog, sourceKey);
  if (!root) return [];
  const result: PathOption[] = [];
  const addFields = (entity: CatalogEntity, prefix: string, group: string) => entity.fields
    .filter((field) => field.filterable && !field.sensitive)
    .forEach((field) => result.push({ path: prefix + field.key, label: label(field), group, isDate: field.type === "DATE" }));
  const walkOne = (entity: CatalogEntity, prefix: string, group: string, depth: number) => {
    entity.relations.filter((relation) => relation.cardinality === "ONE").forEach((relation) => {
      const target = catalog.entities[relation.entity];
      if (!target || depth > 1) return;
      const title = group ? `${group} › ${label(relation)}` : label(relation);
      addFields(target, `${prefix}${relation.key}.`, title);
      walkOne(target, `${prefix}${relation.key}.`, title, depth + 1);
    });
  };
  addFields(root, "", label(root));
  walkOne(root, "", "", 0);
  root.relations.filter((relation) => relation.cardinality === "MANY" && catalog.entities[relation.entity])
    .forEach((relation) => result.push({
      path: relation.key, label: countLabel(label(relation)), group: label(root), isDate: false,
    }));
  return result;
}

/** What SUM/AVG/MIN/MAX can be calculated over: numbers of the subject or ONE relations, and relation counts. */
export function measureOptions(catalog: Catalog, sourceKey: string,
                               label: (item: { labelKey: string; defaultLabel: string }) => string,
                               countLabel: (name: string) => string): PathOption[] {
  return groupOptions(catalog, sourceKey, label, countLabel).filter((option) => {
    const resolved = resolvePath(catalog, sourceKey, option.path);
    return resolved !== null && (!resolved.field || resolved.field.type === "NUMBER");
  });
}

export function setMode(definition: ReportSpec, mode: "TABLE" | "SUMMARY"): ReportSpec {
  if (mode === "TABLE") return { ...definition, summary: null };
  if (definition.summary) return definition;
  return { ...definition, summary: { groups: [], measures: [{ kind: "COUNT" }], sort: [] } };
}

function updateSummary(definition: ReportSpec, update: (summary: SummarySpec) => SummarySpec): ReportSpec {
  const summary = definition.summary ?? { groups: [], measures: [{ kind: "COUNT" }], sort: [] };
  return { ...definition, summary: update(summary) };
}

export function setSummaryGroup(definition: ReportSpec, index: number, group: SummaryGroupSpec | null): ReportSpec {
  return updateSummary(definition, (summary) => {
    const groups = [...summary.groups];
    if (group === null) groups.splice(index, 1);
    else if (index >= groups.length) groups.push(group);
    else groups[index] = group;
    // Group keys shift when one is removed: keep only measure sorting then.
    const sort = group === null ? summary.sort.filter((item) => item.key.startsWith("m")) : summary.sort;
    return { ...summary, groups: groups.slice(0, MAX_SUMMARY_GROUPS), sort };
  });
}

export function addMeasure(definition: ReportSpec, measure: MeasureSpec): ReportSpec {
  return updateSummary(definition, (summary) => summary.measures.length >= MAX_MEASURES
    ? summary
    : { ...summary, measures: [...summary.measures, measure] });
}

export function removeMeasure(definition: ReportSpec, index: number): ReportSpec {
  return updateSummary(definition, (summary) => {
    const measures = summary.measures.filter((_, i) => i !== index);
    return {
      ...summary,
      measures: measures.length ? measures : [{ kind: "COUNT" as MeasureKind }],
      sort: summary.sort.filter((item) => !item.key.startsWith("m")),
    };
  });
}

/** One sort for a summary: by a group value or a calculation. */
export function setSummarySort(definition: ReportSpec, key: string | null, direction: SortDirection): ReportSpec {
  return updateSummary(definition, (summary) => ({ ...summary, sort: key ? [{ key, direction }] : [] }));
}

/** Days in a month, for the end of a month period. */
const lastDay = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();
const pad = (value: number) => String(value).padStart(2, "0");

/**
 * The rows behind one group of a summary: the same report as a table, with the group's values as extra
 * conditions. Columns: the report's own table columns, else the subject's defaults.
 */
export function drillDefinition(catalog: Catalog, definition: ReportSpec, groupValues: Record<string, unknown>):
  ReportSpec | null {
  const summary = definition.summary;
  if (!summary) return null;
  const extra: ConditionSpec[] = [];
  summary.groups.forEach((group, index) => {
    const value = groupValues[`g${index}`];
    const resolved = resolvePath(catalog, definition.source, group.path);
    if (!resolved) return;
    const empty = value === null || value === undefined || value === "";
    if (!resolved.field) {
      extra.push({ path: group.path, operator: "EQUALS", value: Number(value ?? 0) });
      return;
    }
    if (group.bucket && !empty) {
      const number = Number(value);
      if (group.bucket === "YEAR") {
        extra.push({ path: group.path, operator: "BETWEEN", value: [`${number}-01-01`, `${number}-12-31`] });
      } else if (group.bucket === "SCHOOL_YEAR") {
        extra.push({ path: group.path, operator: "BETWEEN", value: [`${number}-08-01`, `${number + 1}-07-31`] });
      } else {
        const year = Math.floor(number / 100);
        const month = number % 100;
        extra.push({ path: group.path, operator: "BETWEEN",
          value: [`${year}-${pad(month)}-01`, `${year}-${pad(month)}-${pad(lastDay(year, month))}`] });
      }
      return;
    }
    switch (resolved.field.type) {
      case "BOOLEAN":
        extra.push({ path: group.path, operator: value ? "IS_TRUE" : "IS_FALSE" });
        break;
      case "CHOICE":
        extra.push(empty ? { path: group.path, operator: "IS_EMPTY" } : { path: group.path, operator: "ANY_OF", value: [String(value)] });
        break;
      case "CLASSROOM": {
        const id = (value as { id?: string } | null)?.id;
        extra.push(id ? { path: group.path, operator: "ANY_OF", value: [id] } : { path: group.path, operator: "IS_EMPTY" });
        break;
      }
      case "DATE":
        extra.push(empty ? { path: group.path, operator: "IS_EMPTY" } : { path: group.path, operator: "ON", value: String(value) });
        break;
      case "NUMBER":
        extra.push(empty ? { path: group.path, operator: "IS_EMPTY" } : { path: group.path, operator: "EQUALS", value: Number(value) });
        break;
      default:
        extra.push(empty ? { path: group.path, operator: "IS_EMPTY" } : { path: group.path, operator: "EQUALS", value: String(value) });
    }
  });
  const source = catalog.sources.find((item) => item.key === definition.source);
  const columns = definition.columns.length > 0
    ? definition.columns
    : (source?.defaultColumns ?? [])
      .filter((column) => resolvePath(catalog, definition.source, column.path))
      .map((column) => ({ path: column.path }));
  const filter = combineFilters(definition.filter, { match: "ALL", conditions: extra });
  if (!filter) return null;
  return { ...definition, summary: null, columns, filter };
}

/**
 * The report's own conditions AND extra ones (a drill-down, or filters added while viewing), within the one level
 * of condition groups the backend accepts. Null when that cannot be expressed (the report's own conditions are
 * "at least one" and already have groups).
 */
export function combineFilters(own: FilterGroupSpec, extra: FilterGroupSpec): FilterGroupSpec | null {
  const extraGroupsIn = extra.groups ?? [];
  if (extra.conditions.length === 0 && extraGroupsIn.length === 0) return own;
  const extraAll = extra.match === "ALL" || extra.conditions.length + extraGroupsIn.length === 1;
  const extraConditions = extraAll ? extra.conditions : [];
  const extraGroups = extraAll ? extraGroupsIn : [{ match: "ANY" as const, conditions: extra.conditions }];
  if (!extraAll && extraGroupsIn.length > 0) return null;
  const ownGroups = own.groups ?? [];
  // "All", or "at least one" of a single condition or group (which means that one), can simply be extended.
  if (own.match === "ALL" || own.conditions.length + ownGroups.length <= 1) {
    return { match: "ALL", conditions: [...own.conditions, ...extraConditions], groups: [...ownGroups, ...extraGroups] };
  }
  if (ownGroups.length > 0) return null;
  return {
    match: "ALL",
    conditions: extraConditions,
    groups: [{ match: "ANY", conditions: own.conditions }, ...extraGroups],
  };
}

/** The text fields shown in this report (its columns, or a summary's groups) that a search looks through. */
export function searchablePaths(catalog: Catalog, definition: ReportSpec): string[] {
  const paths = definition.summary
    ? definition.summary.groups.map((group) => group.path)
    : definition.columns.map((column) => column.path);
  return Array.from(new Set(paths)).filter((path) => {
    const resolved = resolvePath(catalog, definition.source, path);
    return Boolean(resolved?.field?.filterable) && operatorsFor(catalog, resolved).includes("CONTAINS");
  });
}

/**
 * A quick search: rows where at least one of the shown text fields contains the text.
 * Null when there is nothing to search for, or no field to search in.
 */
export function searchGroup(catalog: Catalog, definition: ReportSpec, text: string, room = MAX_CONDITIONS): FilterGroupSpec | null {
  const value = text.trim().slice(0, 200);
  if (!value) return null;
  const paths = searchablePaths(catalog, definition).slice(0, Math.max(0, room));
  if (paths.length === 0) return null;
  return { match: "ANY", conditions: paths.map((path) => ({ path, operator: "CONTAINS", value })) };
}

export function sameDefinition(a: ReportSpec, b: ReportSpec): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
